import React, { useEffect, useState } from 'react';
import { ErrorIcon, NewUpdateIcon, PairedIcon } from '../Common/Icons';
import { useSelector } from 'react-redux';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';

/**
 * Displays a draggable rectangle within the parent component which can be used for setting pan and zoom for a device.
 * 3 modes are supported: "Room View": 2160p, "Normal": 1080p, "Focus": 720p.
 * Co-ordinates of the start (top-left) of the rectangle along-with the selected mode is to be sent to the device for setting.
 */
const focusRectDim = { width: 1280, height: 720 };
const normalRectDim = { width: 1920, height: 1080 };

const CameraCropControl = ({
  containerRef,
  setShowZoomModeBar,
  showZoomModeBar,
  setIsCroppingEnabled,
  principalDeviceUUID,
}) => {
  const trans = useSelector(translator);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState(normalRectDim);
  const [selected, setSelected] = useState('Room View');
  const [appliedMode, setAppliedMode] = useState('Room View');
  const deviceManagementApi = useSelector((state) => state.apis.deviceManagementApi);

  useEffect(() => {
    handleButtonClick(
      {
        width: containerRef.current?.clientWidth || 1000,
        height: containerRef.current?.clientHeight || 800,
      },
      'Room View'
    );
    sendCropCommand('Room View');
    setShowZoomModeBar(true);
    setAppliedMode('Room View');
    setSelected('Room View');
    setPosition({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (isDragging && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const newX = Math.max(
          0,
          Math.min(
            containerRect.width - size.width,
            event.clientX - containerRect.left - dragOffset.x
          )
        );
        const newY = Math.max(
          0,
          Math.min(
            containerRect.height - size.height,
            event.clientY - containerRect.top - dragOffset.y
          )
        );
        setPosition({ x: newX, y: newY });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, size, dragOffset, containerRef]);

  const handleMouseDown = (event) => {
    if (containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      setIsDragging(true);
      setDragOffset({
        x: event.clientX - containerRect.left - position.x,
        y: event.clientY - containerRect.top - position.y,
      });
    }
  };

  const handleButtonClick = (newSize, label) => {
    if (containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      setSelected(label); //update the selection
      if (label === 'Room View') {
        setShowZoomModeBar(true);
        setSize({ width: containerRect.width, height: containerRect.height });
        setPosition({ x: 0, y: 0 });
        setAppliedMode('Room View');
        sendCropCommand('Room View');
        return;
      } else {
        // Scale based on 3840x2160 resolution
        const factorX = containerRect.width / 3840;
        const factorY = containerRect.height / 2160;
        const scaledWidth = newSize.width * factorX;
        const scaledHeight = newSize.height * factorY;
        const maxX = (containerRect.width - scaledWidth) / 2;
        const maxY = (containerRect.height - scaledHeight) / 2;
        const newX = Math.max(0, Math.min(position.x, maxX));
        const newY = Math.max(0, Math.min(position.y, maxY));
        setSize({ width: scaledWidth, height: scaledHeight });
        setPosition({ x: maxX, y: maxY });
      }
      setSelected(label);
      setShowZoomModeBar(true);
    }
  };

  const handleResize = () => {
    if (setShowZoomModeBar) setShowZoomModeBar(false);
    if (selected !== 'Room View') {
      sendCropCommand(selected); // Send API only for Normal and Focus
    }
    setIsCroppingEnabled(false);
    setAppliedMode(selected); //apply the mode now
  };

  const handleCancel = () => {
    setShowZoomModeBar(false);
    setIsCroppingEnabled(false);
  };

  const sendCropCommand = (mode = appliedMode) => {
    if (!deviceManagementApi?.cameraCropControl) return;

    const modeValue = mode === 'Focus' ? 720 : mode === 'Normal' ? 1080 : 2160;

    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    let cropX = 0;
    let cropY = 0;
    if (mode === 'Focus' || mode === 'Normal') {
      const factorX = 3840 / containerWidth;
      const factorY = 2160 / containerHeight;
      cropX = position.x * factorX;
      cropY = position.y * factorY;
    }
    const cropPayload = {
      camera_crop_control: {
        cropx: cropX,
        cropy: cropY,
        // cropx: 1500,
        // cropy: 900,
        mode: modeValue,
      },
    };
    if (mode === 'Room View') {
      cropPayload.camera_crop_control.cropx = 0;
      cropPayload.camera_crop_control.cropy = 0;
    }
    deviceManagementApi.cameraCropControl(
      principalDeviceUUID,
      cropPayload,
      (error, data, response) => {
        if (error) {
          console.error('API Error:', error);
        }
      }
    );
  };

  return (
    <div style={{ position: 'absolute', width: '100%', height: '100%' }}>
      {showZoomModeBar && (
        <>
          {selected !== 'Room View' && (
            <div
              onMouseDown={handleMouseDown}
              style={{
                width: `${size.width}px`,
                height: `${size.height}px`,
                border: '5px solid green',
                backgroundColor: 'transparent',
                position: 'absolute',
                left: `${position.x}px`,
                top: `${position.y}px`,
                cursor: 'grab',
              }}
            ></div>
          )}
          {!isDragging && selected !== 'Room View' && (
            <div className="zoom-mode-information">
              <div className="zoom-mode-information-block">
                <ErrorIcon className="zoom-mode-information-icon" fillColor="#808080" />
                <span className="zoom-mode-information-label">
                  {trans('DRAGTHEGREENRECTANGLETOOBTAINTHEDESIREDFRAMING')}
                </span>
              </div>
            </div>
          )}
          <div className="zoom-mode-bar">
            <button
              onClick={() => {
                handleButtonClick(
                  {
                    width: containerRef.current?.clientWidth || 1000,
                    height: containerRef.current?.clientHeight || 800,
                  },
                  'Room View'
                );
                sendCropCommand('Room View');
                setShowZoomModeBar(true);
              }}
              className={`zoom-mode-button ${selected === 'Room View' ? 'selected' : ''}`}
            >
              {trans('ROOMVIEW')}
            </button>
            <button
              onClick={() => handleButtonClick(normalRectDim, 'Normal')}
              className={`zoom-mode-button ${selected === 'Normal' ? 'selected' : ''}`}
            >
              {trans('NORMAL')}
            </button>
            <button
              onClick={() => handleButtonClick(focusRectDim, 'Focus')}
              className={`zoom-mode-button ${selected === 'Focus' ? 'selected' : ''}`}
            >
              {trans('FOCUS')}
            </button>
            <button onClick={handleCancel} className="zoom-mode-cancel-button">
              <div className="cancel-block">
                <NewUpdateIcon className="cancel-icon" />
                <span className="cancel-button-label">{trans('CANCEL')}</span>
              </div>
            </button>
            <button
              onClick={handleResize}
              className="zoom-mode-resize-button"
              disabled={selected === 'Room View'}
            >
              <div className="resize-block">
                <NewUpdateIcon className="resize-icon" />
                <span className="resize-button-label">{trans('APPLY')}</span>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CameraCropControl;
